import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { trackPageView } from '../utils/analytics';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiMail, FiClock, FiArrowLeft, FiMessageSquare, FiCalendar } = FiIcons;

const ContactThankYou = () => {
  useEffect(() => {
    trackPageView('Contact Thank You');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Back to home link */}
      <Link to="/" className="fixed top-4 left-4 z-50 bg-black-900/80 p-2 rounded-full hover:bg-black-900 transition-colors">
        <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-white" />
      </Link>

      {/* Confetti Effect */}
      <div className="fixed inset-0 pointer-events-none z-10">
        {[...Array(45)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-r from-indigo-400 to-purple-500 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: -10,
              rotate: 0,
              scale: Math.random() * 0.5 + 0.5
            }}
            animate={{
              y: window.innerHeight + 10,
              rotate: 360,
              scale: 0
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              delay: Math.random() * 2,
              ease: "easeOut"
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-24 h-24 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <SafeIcon icon={FiCheck} className="w-12 h-12 text-white" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-6xl font-bold text-black-900 mb-6 font-serif"
          >
            Message Received! 💌
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-black-700 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Thank you for reaching out! We've received your message and appreciate you taking the time to contact us about your business needs.
          </motion.p>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-black-900 mb-4 font-serif">What Happens Next?</h2>
            <div className="w-16 h-1 bg-black-900 mx-auto"></div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-black-50 p-6 rounded-lg text-center"
            >
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMail} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Email Confirmation</h3>
              <p className="text-black-700">You'll receive an email confirmation that we've received your inquiry.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="bg-black-50 p-6 rounded-lg text-center"
            >
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMessageSquare} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Personal Review</h3>
              <p className="text-black-700">I'll personally review your message and understand your specific business needs.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="bg-black-50 p-6 rounded-lg text-center"
            >
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCalendar} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Direct Response</h3>
              <p className="text-black-700">I'll reach out directly within 48 business hours with a thoughtful response.</p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.4 }}
            className="bg-black-900 text-white p-8 rounded-lg text-center"
          >
            <h3 className="text-2xl font-bold mb-4">My 24-Hour Promise</h3>
            <p className="text-lg text-black-200 mb-6">
              If you reach out on a business day, I'll reply within 24 hours. No bots, no canned replies—just a real conversation about what matters to your business.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link to="/problem-solving">
                <button className="px-6 py-3 bg-white text-black-900 rounded-lg font-semibold hover:bg-black-100 transition-colors">
                  Book a Problem-Solving Session
                </button>
              </Link>
              <Link to="/free-landing-page">
                <button className="px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-black-900 transition-colors">
                  Get Free Landing Page
                </button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.6 }}
            className="mt-8 text-center"
          >
            <div className="flex items-center justify-center space-x-2 text-black-600 mb-4">
              <SafeIcon icon={FiClock} className="w-5 h-5" />
              <span>Expected response time: 48 business hours or less</span>
            </div>
            <Link to="/">
              <button className="px-6 py-3 bg-black-900 text-white rounded-lg font-semibold hover:bg-black-800 transition-colors">
                Return to Homepage
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactThankYou;